import { useMemo, useState } from 'react'
import { Bell, Check, UserPlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { DataTable, EmptyMessageWithLoader } from '@/components/ui/data-table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useDiscordChannelDetails,
  useNotificationTemplates,
} from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import Card from 'app/frontend/base/components/Card'
import CopyableApiKeyValue from 'app/frontend/base/components/CopyableApiKeyValue'
import { imageUrl } from 'app/frontend/base/helpers'
import { infoByType } from 'app/frontend/base/pages/UserDashboard/subpages/UserLinkedAccounts/components/LinkedAccount'
import NotificationForm from 'app/frontend/base/pages/TeamDashboard/subpages/TeamSettings/components/NotificationForm'
import TemplateActionButtons from 'app/frontend/base/pages/TeamDashboard/subpages/TeamSettings/components/TemplateActionButtons'
import { buildNotificationTemplateColumns } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamSettings/components/notificationTemplateColumns'
import StepLayout from '../StepLayout'
import StepFooter from '../StepFooter'
import { StepProps } from '../types'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'

const CANDIDATE_TEMPLATE_TYPE = 'Template::CandidateUpdate'

const NotificationsStep = ({ goNext, goBack, isFirst }: StepProps) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const gameVersion = useGameVersion()
  const [openTemplate, setOpenTemplate] = useState<
    Partial<wowaudit.NotificationTemplate> | undefined
  >()

  const {
    data: templates,
    error,
    isPending,
    isLoadingError,
  } = useNotificationTemplates(team.id)
  const { data: discordChannelDetails } = useDiscordChannelDetails(
    templates
      ?.filter((template) => template.targetAddress)
      ?.map((template) => template.targetAddress as string) ?? [],
  )

  const candidateTemplate = templates?.find(
    (template) => template.type === CANDIDATE_TEMPLATE_TYPE,
  )

  const columns: ColumnDef<wowaudit.NotificationTemplate>[] = useMemo(
    () => [
      ...buildNotificationTemplateColumns({
        team,
        discordChannelDetails: discordChannelDetails ?? [],
        t,
      }),
      {
        header: '',
        id: 'actions',
        cell: ({ row }) => (
          <TemplateActionButtons
            template={row.original}
            onEdit={setOpenTemplate}
          />
        ),
      },
    ],
    [team, discordChannelDetails, t],
  )

  return (
    <StepLayout
      icon={Bell}
      title='Configure notifications'
      description='Set up templates to automatically send reminders and alerts to your team.'
    >
      {gameVersion.has('guild_candidates') ? (
        <div className='mb-6 flex flex-col gap-4 rounded-lg border border-primary/40 bg-primary/5 p-4 sm:flex-row sm:items-center'>
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary'>
            <UserPlus className='h-5 w-5' />
          </div>
          <div className='flex grow flex-col gap-1'>
            <div className='flex items-center gap-2'>
              <span className='font-semibold'>
                Get notified about new candidates
              </span>
              {candidateTemplate ? (
                <Badge variant='success' className='gap-1'>
                  <Check className='h-3 w-3' />
                  Configured
                </Badge>
              ) : null}
            </div>
            <p className='mb-0 text-sm text-muted-foreground'>
              Send a message to Discord or by email whenever a player signs up
              as a candidate, or an admin adds one.
            </p>
          </div>
          <Button
            type='button'
            size='sm'
            variant={candidateTemplate ? 'outline' : 'default'}
            className='shrink-0'
            onClick={() =>
              setOpenTemplate(
                candidateTemplate ?? { type: CANDIDATE_TEMPLATE_TYPE },
              )
            }
          >
            {candidateTemplate ? 'Edit' : 'Set up'}
          </Button>
        </div>
      ) : null}

      <div className='mb-4 flex items-center justify-between gap-4'>
        <Button type='button' size='sm' onClick={() => setOpenTemplate({})}>
          Add template
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={templates ?? []}
        includeHeader={true}
        emptyMessage={() => (
          <EmptyMessageWithLoader
            emptyMessageKey='notifications.empty-message'
            isPending={isPending}
            isLoadingError={isLoadingError}
            error={error}
            t={t}
          />
        )}
      />

      <Card className='mt-8 flex flex-col gap-4'>
        <div className='flex items-center justify-between gap-4'>
          <h5 className='mb-0 flex items-center gap-2'>
            <img src={imageUrl(infoByType.Discord.logo)} className='w-5' />
            Install the Discord bot
          </h5>
          <a
            href='/get_discord_bot'
            target='_blank'
            rel='noopener noreferrer'
            className={buttonVariants({ size: 'sm' })}
          >
            Add to server
          </a>
        </div>

        <p className='mb-0 text-sm text-muted-foreground'>
          Discord notifications are delivered with the wowaudit bot. After
          adding it to your server, run <strong>/setup</strong> and paste the
          below API key when prompted.
        </p>

        <CopyableApiKeyValue
          value={team.privateKey}
          tooltipContent={t('dashboard.team.settings.click-to-copy')}
        />
      </Card>

      <StepFooter
        showBack={!isFirst}
        onBack={goBack}
        onContinue={goNext}
        onSkip={goNext}
      />

      <Dialog
        open={!!openTemplate}
        onOpenChange={(open) => {
          if (!open) setOpenTemplate(undefined)
        }}
      >
        <DialogContent
          className='max-w-3xl'
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              {openTemplate?.id ? 'Edit notification' : 'New notification'}
            </DialogTitle>
          </DialogHeader>

          {openTemplate ? (
            <NotificationForm
              key={openTemplate.id ?? 'new'}
              template={openTemplate}
              setTemplate={setOpenTemplate}
              backLabel='Close'
              inModal={true}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </StepLayout>
  )
}

export default NotificationsStep
